/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { injectIntl } from 'react-intl';
import { useParams } from 'react-router';
import { TextField } from '../../components/TextFieldInput';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import axios from 'axios';
import { TextArea } from '../../components/TextAreaInput';
import { Drawer } from '../../components/Drawer';
import { Button } from '../../components/Button';
import { SearchInput } from '../../components/SearchInput';
import { Map } from '../../components/map/Map';
import { editFacilityStyles } from '../../components/map/mapProps';
import { centroid, booleanWithin } from '@turf/turf';
import RemoveDialog from '../../components/RemoveDialog';
import MetadataMenu from '../../components/MetadataMenu';
import { Loading } from '../../components/Loading';
import { variants } from '../../utils/motionVariants';
import { CardHeader } from '../../components/CardHeader';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import { MenuOptions } from '../../components/MenuOptions';
import { usePermissions } from '../../contexts/PermissionsContext';
import { AddButton } from '../../components/AddButton';
import compareStrings from '../../utils/compareStrings';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { v4 } from 'uuid';
import ErrorDialog from '../../components/ErrorDialog';

const LABEL_SIZE = 80;

function getSchema(intl) {
  return Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[!-ÿ\s]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.NAME.REGEX' })
      ),
    description: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
    externalId: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[a-z0-9-.]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.CODE.REGEX' })
      ),
    geoJson: Yup.object().required(
      intl.formatMessage({ id: 'AREAS.FACILITIES.VALIDATION.TITLE' })
    ),
  });
}

function FacilitiesEdit({ intl, history }) {
  let { areaId, facilityId } = useParams();
  const [area, setArea] = useState({});
  const [facility, setFacility] = useState({
    name: '',
    description: '',
    externalId: v4(),
  });
  const [floorPlans, setFloorPlans] = useState([]);
  const [filteredFloorPlans, setFilteredFloorPlans] = useState([]);
  const [zones, setZones] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [controller, setController] = useState(null);
  const [menuOpen, setMenuOpen] = useState({
    floorplans: false,
    metadatas: false,
    zones: false,
  });
  const mapRef = useRef(null);
  const { hasPermission } = usePermissions();
  const { setPaths } = useBreadcrumb();

  if (facilityId === 'new') facilityId = null;

  useEffect(() => {
    async function getData() {
      setIsLoading(true);
      const promises = [];
      promises.push(axios.get(`/areas/${areaId}`));
      if (facilityId) {
        promises.push(axios.get(`/areas/${areaId}/facilities/${facilityId}`));
        promises.push(
          axios.get(`/areas/${areaId}/facilities/${facilityId}/floor-plans`)
        );
        promises.push(axios.get(`/zones?facilityId=${facilityId}`));
      }
      try {
        const [
          areaPromise,
          facilityPromise,
          floorPlansFacility,
          zonesPromise,
        ] = await Promise.all(promises);
        if (areaPromise.data) setArea(areaPromise.data);
        if (facilityPromise?.data) {
          setFacility(facilityPromise.data);
          setPaths([
            { title: intl.formatMessage({ id: 'FACILITIES' }) },
            {
              title: intl.formatMessage({ id: 'AREAS.TITLE' }),
              pathname: '/geo-planner/areas',
            },
            {
              title: facilityPromise.data.areaName,
              pathname: `/geo-planner/areas/${areaId}`,
            },
            {
              title: facilityPromise.data.name,
              pathname: `/geo-planner/areas/${areaId}/facilities/${facilityId}`,
            },
          ]);
        }
        if (floorPlansFacility?.data) {
          setFloorPlans(floorPlansFacility.data);
          setFilteredFloorPlans(floorPlansFacility.data);
        }
        if (zonesPromise?.data) {
          setZones(zonesPromise.data);
          setFilteredZones(zonesPromise.data);
        }
      } finally {
        setIsLoading(false);
      }
    }
    getData();
  }, [areaId, facilityId]);

  useEffect(() => {
    if (controller && mapRef.current) {
      if (!facilityId) {
        controller.clear();
        mapRef.current.jumpTo({
          center: [0, 0],
          zoom: 2,
        });
      }
      if (area.id) {
        const coords = JSON.parse(area.mapCentroid).coordinates;
        mapRef.current.jumpTo({
          center: [coords[1], coords[0]],
          zoom: area.zoomLevel,
        });
        if (!mapRef.current.getSource(area.name)) addSource();
        mapRef.current.on('style.load', addSource);
      }
      if (facility.id) {
        const coords = JSON.parse(facility.mapCentroid).coordinates;
        mapRef.current.jumpTo({
          center: [coords[1], coords[0]],
          zoom: facility.zoomLevel,
        });
        controller.add(facility.geoJson);
      }
    }
  }, [controller, area, facility, mapRef]);

  const formik = useFormik({
    initialValues: facility,
    enableReinitialize: true,
    validationSchema: getSchema(intl),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      if (!booleanWithin(formik.values.geoJson, area.geoJson)) {
        return;
      } else formik.setFieldError('geoJson', null);
      values.zoomLevel = mapRef.current.getZoom();
      const coords = centroid(values.geoJson.geometry).geometry.coordinates;
      values.mapCentroid = {
        lat: coords[1],
        long: coords[0],
      };
      if (!facilityId) {
        await axios.post(`/areas/${areaId}/facilities`, values);
        history.push(`/geo-planner/facilities`);
      } else {
        delete values.areaCentroid;
        delete values.locality;
        delete values.adminRegion;
        delete values.id;
        delete values['countryISO3166-2'];
        await axios.put(`/areas/${areaId}/facilities/${facilityId}`, values);
        history.push(`/geo-planner/facilities`);
      }
      setSubmitting(false);
    },
  });

  function addSource() {
    mapRef.current.addSource(area.name, {
      type: 'geojson',
      data: area.geoJson,
    });
    mapRef.current.addLayer({
      id: area.name,
      type: 'line',
      source: area.name,
      paint: {
        'line-color': '#f08',
        'line-width': 1,
        'line-dasharray': [9, 1],
      },
    });
  }

  function closeSidebar() {
    setMenuOpen({ facilities: false, metadatas: false });
    setFilteredFloorPlans(floorPlans);
    setFilteredZones(zones);
  }

  function closeModal() {
    setOpen(false);
  }

  async function removeFacilities() {
    closeModal();
    try {
      setIsLoading(true);
      await axios.delete(`/areas/${areaId}/facilities/${facilityId}`);
      history.push(`/geo-planner/areas/${areaId}`);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400)
        setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  }

  function changeText(type) {
    return val => {
      if (type === 'floorPlan')
        setFilteredFloorPlans(
          floorPlans.filter(x => compareStrings(JSON.stringify(x), val))
        );
      else
        setFilteredZones(
          zones.filter(x => compareStrings(JSON.stringify(x), val))
        );
    };
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={variants}
      className="card flex-row-fluid h-100"
    >
      <CardHeader
        title={intl.formatMessage(
          { id: 'AREAS.FACILITIES.TITLE' },
          {
            name:
              formik.values?.name || intl.formatMessage({ id: 'NEW_FACILITY' }),
          }
        )}
        subtitle={intl.formatMessage({ id: 'AREAS.FACILITIES.SUBTITLE' })}
      />
      <div className="card-body" id="facility-container">
        <Loading isLoading={isLoading} />
        <div className="d-flex flex-wrap">
          <div className="col-sm-12 col-md-12 col-lg-6 px-0">
            <div className="col-sm-12 col-md-12 col-lg-5 px-0">
              <div className="col-sm-12 col-md-12 col-lg-12 px-0">
                <TextField
                  labelClasses="font-weight-bolder"
                  field="externalId"
                  inputSize="100%"
                  labelMinSize={LABEL_SIZE}
                  formik={formik}
                  label={intl.formatMessage({ id: 'AREAS.FACILITIES.ID' })}
                />
              </div>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-10 px-0">
              <TextField
                field="name"
                labelClasses="font-weight-bolder"
                inputSize="100%"
                labelMinSize={LABEL_SIZE}
                formik={formik}
                label={intl.formatMessage({ id: 'AREAS.FACILITIES.NAME' })}
              />
            </div>
          </div>
          <div className="col-sm-12 col-md-12 col-lg-6 px-0">
            <TextArea
              labelClass="font-weight-bolder"
              height={105}
              field="description"
              inputSize="100%"
              labelMinSize={LABEL_SIZE}
              formik={formik}
              label={intl.formatMessage({ id: 'DESCRIPTION' })}
            />
          </div>
        </div>
        <div className="col-12 d-flex justify-content-between px-0">
          <div className="d-flex">
            {hasPermission('facilities*', 'U') && (
              <Button
                classes="w-75px px-12 py-2 my-1 d-flex align-items-center justify-content-center btn btn-primary"
                type="submit"
                formik={formik}
                onClick={formik.handleSubmit}
                title={intl.formatMessage({ id: 'SAVE' })}
              />
            )}
            <Button
              classes="w-75px cancel-bg ml-3 cancel-btn cancel-text-color px-4"
              type="button"
              onClick={() => history.push(`/geo-planner/areas/${areaId}`)}
              title={intl.formatMessage({ id: 'CANCEL' })}
            />
            {facilityId && hasPermission('facilities*', 'D') && (
              <Button
                classes="w-75px bg-danger ml-3 px-4"
                type="button"
                onClick={() => setOpen(true)}
                title={intl.formatMessage({ id: 'DELETE' })}
              />
            )}
          </div>
          <div>
            {hasPermission('metadata*', 'R') && (
              <Button
                classes="px-5"
                logoSrc={'/media/svg/senseworks/metadata.svg'}
                disabled={!facilityId ? true : false}
                type="button"
                onClick={() =>
                  setMenuOpen({
                    metadatas: true,
                    floorplans: false,
                    zones: false,
                  })
                }
                title={intl.formatMessage({ id: 'METADATA' })}
              />
            )}
            {hasPermission('floor-plans*', 'R') && (
              <Button
                classes="px-5 ml-3"
                logoSrc={'/media/svg/senseworks/floormap.svg'}
                disabled={!facilityId ? true : false}
                type="button"
                onClick={() =>
                  setMenuOpen({
                    metadatas: false,
                    floorplans: true,
                    zones: false,
                  })
                }
                title={intl.formatMessage({ id: 'FLOORPLANS' })}
              />
            )}
            {hasPermission('zones*', 'R') && (
              <Button
                classes="px-5 ml-3"
                logoSrc={'/media/svg/senseworks/zone.svg'}
                disabled={!facilityId ? true : false}
                type="button"
                onClick={() =>
                  setMenuOpen({
                    metadatas: false,
                    floorplans: false,
                    zones: true,
                  })
                }
                title={intl.formatMessage({ id: 'ZONES' })}
              />
            )}
          </div>
        </div>
        <div className="mt-3 col-12 px-0">
          <Map
            formik={formik}
            mapRef={mapRef}
            mapStyle={editFacilityStyles}
            setController={setController}
            mapHeight="calc(60vh - 15px)"
          >
            {formik.errors['geoJson'] ? (
              <div
                className="center-box d-flex flex-center flex-column"
                style={{ pointerEvents: 'none' }}
              >
                <div>{formik.errors['geoJson']}</div>
                <div>
                  {intl.formatMessage({
                    id: 'AREAS.FACILITIES.VALIDATION.SUBTITLE',
                  })}
                </div>
              </div>
            ) : null}
          </Map>
        </div>
      </div>
      <RemoveDialog
        open={open}
        onCancel={closeModal}
        onClose={closeModal}
        onConfirm={removeFacilities}
        name={facility.name}
        message="ACTIONS.REMOVE.FACILITY.BODY"
        title="ACTIONS.REMOVE.FACILITY.TITLE"
      />
      {hasPermission('metadata*', 'R') && (
        <Drawer
          open={menuOpen.metadatas}
          onClose={closeSidebar}
          containerId="floormap-container"
        >
          <div className="flex-row-fluid h-100" style={{ width: 450 }}>
            <MetadataMenu
              intl={intl}
              hasPermission={hasPermission}
              type="facility"
              id={facilityId}
            />
          </div>
        </Drawer>
      )}
      {hasPermission('floor-plans*', 'R') && (
        <Drawer
          open={menuOpen.floorplans}
          onClose={closeSidebar}
          containerId="facility-container"
        >
          <div className="h-100" style={{ width: 350 }}>
            <div className="card h-100">
              <CardHeader
                title={intl.formatMessage({ id: 'FLOORPLANS' })}
                subtitle={intl.formatMessage({
                  id: 'AREAS.FACILITIES.FLOORPLANS.SUBTITLE',
                })}
                iconPath={toAbsoluteUrl('/media/svg/senseworks/floormap.svg')}
                btn={
                  hasPermission('floor-plans*', 'C') && (
                    <AddButton
                      onClick={() =>
                        history.push(
                          `/geo-planner/areas/${areaId}/facilities/${facilityId}/floor-plans`,
                          { from: '' }
                        )
                      }
                    />
                  )
                }
              />
              <div className="d-flex flex-column h-100">
                <div
                  className="areas-bg-grey px-3 py-2"
                  style={{ borderBottom: 'solid 1px #AAAAAA' }}
                >
                  <SearchInput onChange={changeText('floorPlan')} />
                </div>
                <div className="d-flex flex-column justify-content-between h-100">
                  <div>
                    {filteredFloorPlans.map(op => (
                      <MenuOptions
                        key={op.id}
                        description={op.description}
                        name={op.name}
                        to={`/geo-planner/areas/${areaId}/facilities/${facilityId}/floor-plans/${op.id}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      )}
      {hasPermission('zones*', 'R') && (
        <Drawer
          open={menuOpen.zones}
          onClose={closeSidebar}
          containerId="facility-container"
        >
          <div className="h-100" style={{ width: 350 }}>
            <div className="card h-100">
              <CardHeader
                title={intl.formatMessage({ id: 'ZONES' })}
                subtitle={intl.formatMessage({
                  id: 'AREAS.FACILITIES.ZONES.SUBTITLE',
                })}
                iconPath={toAbsoluteUrl('/media/svg/senseworks/zone.svg')}
              />
              <div className="d-flex flex-column h-100">
                <div
                  className="px-3 py-2"
                  style={{ borderBottom: 'solid 1px #AAAAAA' }}
                >
                  <SearchInput onChange={changeText('zone')} />
                </div>
                <div className="d-flex flex-column justify-content-between h-100">
                  <div>
                    {filteredZones.map(op => (
                      <MenuOptions
                        key={op.zoneId}
                        description={op.description}
                        name={op.name}
                        to={`/geo-planner/areas/${areaId}/facilities/${facilityId}/floor-plans/${op.floorPlanId}?zoneId=${op.zoneId}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      )}
      <ErrorDialog
        onClose={() => setModalOpen(false)}
        open={isModalOpen}
        message="ERRORS.FACILITIES.BAD_REQUEST"
      />
    </motion.div>
  );
}

export default injectIntl(FacilitiesEdit);
